/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Box, Container, IconButton, makeStyles } from "@material-ui/core";
import {
  FilterButton,
  HeaderWithButtons,
  Searchbar,
} from "../components-molecules";
import CssBaseline from "@material-ui/core/CssBaseline";
import { theme } from "../constants/theme";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { InvoiceButtonSection } from "../components-molecules/InvoiceButtonSection";
import { InvoiceGroupItem } from "../components-molecules/InvoiceGroupItem";
import { useDispatch, useSelector } from "react-redux";
import { CircularProggressIndicator } from "../components-atoms/CircularProggressIndicator";
import multiDownload from "multi-download";
import { debounce } from "lodash";
import { InvoiceMerchant } from "../redux/reducers/getInvoiceMerchants";
import { InvoiceShop } from "../redux/reducers/getInvoiceShops";
import { IFilter, IFilterOption } from "../components-molecules/FilterButton";
import { getInvoiceMerchants } from "../redux/actions/getInvoiceMerchants";
import { getAdminInvoices } from "../redux/actions/getAdminInvoices";
import {
  AdminInvoiceGroupResponseModel,
  AdminInvoiceListResponseModel,
} from "../redux/reducers/getAdminInvoices";
import { getInvoiceShops } from "../redux/actions/getInvoiceShops";
import InfiniteScroll from "react-infinite-scroll-component";
import { NoResultsFound } from "../components-molecules/NoResultsFound";
import { GetTemporaryInvoiceUrl } from "../service/temporary-access-service";
import { useTranslation } from 'react-i18next';

export type OrderSortType = "asc" | "desc";
const debounceDuration = 500;
interface Filter extends IFilter {
  Date: Array<Date | null>;
  Merchants: string[];
  Shops: string[];
  InvoiceType: string[];
}
const AdminInvoices = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const pageSize = 10;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<AdminInvoiceGroupResponseModel[]>(
    []
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [invoiceMerchants, invoiceShops, loading]: [
    InvoiceMerchant[],
    InvoiceShop[],
    boolean
  ] = useSelector((state: any) => [
    state.getInvoiceMerchants.data,
    state.getInvoiceShops.data,
    state.getAdminInvoices.loading,
  ]);
  const [filter, setFilter] = useState<Filter>({
    Date: [],
    Merchants: [],
    Shops: [],
    InvoiceType: [],
  });
  const [filterOptions, setFilterOptions] = useState<IFilterOption>({
    Date: [
      { title: "From", value: "" },
      { title: "To", value: "" },
    ],
    Merchants: [],
    Shops: [],
    InvoiceType: [
      { title: t('admin.tradekartOnly'), value: "tradekart" },
      { title: t('admin.tradekartInvoiceFees'), value: "tkcollect" },
      { title: t('admin.shopOnly'), value: "shop" },
      { title: t('admin.merchantFees'), value: "tradekartfee" },
    ],
  });
  const [filterApplied, setFilterApplied] = useState(filtersApplied(filter));

  useLayoutEffect(() => {
    dispatch(getInvoiceMerchants());
    dispatch(getInvoiceShops(true));
  }, [dispatch]);
  useEffect(() => {
    const merchantOptions = invoiceMerchants.map((m) => ({
      title: m.Name,
      value: m.Id,
    }));
    const shopOptions = invoiceShops.map((m) => ({
      title: m.Name,
      value: m.Id,
    }));
    setFilterOptions((prev) => ({
      ...prev,
      Merchants: merchantOptions,
      Shops: shopOptions,
    }));
  }, [dispatch, invoiceMerchants, invoiceShops]);

  useEffect(() => {
    setFilterApplied(filtersApplied(filter));
  }, [filter]);

  const getInvoices = useCallback(
    (
      size = pageSize,
      currentPage = 0,
      prevData: AdminInvoiceGroupResponseModel[] = []
    ) => {
      dispatch(
        getAdminInvoices(
          {
            Page: currentPage,
            Size: size,
            Query: searchQuery,
            Filter: {
              DateFrom: filter.Date?.[0]
                ? new Date(filter.Date[0] as Date)
                : undefined,
              DateTo: filter.Date?.[1]
                ? new Date(filter.Date[1] as Date)
                : undefined,
              Merchants: filter.Merchants,
              Shops: filter.Shops,
              InvoiceType: {
                TradeKart: filter.InvoiceType.includes("tradekart"),
                TradeKartFee: filter.InvoiceType.includes("tradekartfee"),
                TradeKartCollect: filter.InvoiceType.includes("tkcollect"),
                Shop: filter.InvoiceType.includes("shop"),
              },
            },
          },
          (resp) => {
            const response = resp.Result as AdminInvoiceListResponseModel;
            setTotal(response.Total);
            let temp = [...prevData];
            response.InvoiceGroups.forEach((m) => {
              const tempMatch = temp.find((f) => f.OrderNo === m.OrderNo);
              if (tempMatch) {
                temp[temp.indexOf(tempMatch)].Invoices = [
                  ...temp[temp.indexOf(tempMatch)].Invoices,
                  ...m.Invoices,
                ];
              } else {
                temp = [...temp, m];
              }
            });
            setInvoices(temp);
          }
        )
      );
    },
    [dispatch, searchQuery, filter]
  );
  useEffect(() => {
    getInvoices();
  }, [getInvoices]);
  const loadMore = () => {
    getInvoices(pageSize, page + 1, invoices);
    setPage((prev) => prev + 1);
  };
  const filterOnSubmit = (filter) => {
    setFilter(filter as Filter);
  };

  const onCheckboxChange = (data, value) => {
    if (value) {
      setSelectedInvoices((prev) => [...prev, data.InvoiceNo]);
    } else {
      setSelectedInvoices((prev) => [
        ...prev.filter((f) => f !== data.InvoiceNo),
      ]);
    }
  };
  const downloadPdfs = async () => {
    let blobs: Array<any> = [];
    let invoiceNames: Array<String> = [];
    const selectedList = invoices
      .map((m) => m.Invoices)
      .flat()
      .filter((f) => selectedInvoices.includes(f.InvoiceNo));
    selectedList.forEach((item, index: number) => {
      GetTemporaryInvoiceUrl(
        item.InvoiceNo,
        async (temporaryUrl) => {
          const data = await fetch(temporaryUrl, {
            method: "GET",
            mode: "cors",
            headers: { Accept: "form-data" },
          });
        
          const convertedToBlob = await data.blob();
          const convertedToUrl = await window.URL.createObjectURL(
            new Blob([convertedToBlob])
          );
          blobs.push(convertedToUrl);
          invoiceNames.push(item.InvoiceNo);
          if (selectedInvoices.length - 1 === index) {
            multiDownload(blobs, {
              rename: ({ url, index, urls }) => invoiceNames[index] + ".pdf",
            });
          }
        }
      );
    });
  };
  const selectAllClicked = () => {
    setSelectedInvoices(
      invoices
        .map((m) => m.Invoices)
        .flat()
        .map((m) => m.InvoiceNo)
    );
  };
  const clearAllClicked = () => {
    setSelectedInvoices([]);
  };

  return (
    <Box className={classes.container}>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons title={t('admin.invoices')}>
          <HeaderWithButtons.ButtonArea>
            <>
              <FilterButton
                dateKey="Date"
                singleDate
                defaultOption="Date"
                data-automation="filter-button"
                onSubmit={filterOnSubmit}
                inButtonSet
                options={filterOptions}
                filter={filter}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchButton}
                color="primary"
                onClick={() =>
                  searchOpen ? setSearchOpen(false) : setSearchOpen(true)
                }
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
            </>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={searchQuery}
          isOpen={searchOpen}
placeholder={t('admin.searchInvoices')}
          onCancel={() => {
            setSearchOpen(false);
            debounce((e) => setSearchQuery(e), debounceDuration)("");
          }}
          onChange={(event) => {
            debounce(
              (e) => setSearchQuery(e.target.value),
              debounceDuration
            )(event);
          }}
        />
        {loading && page === 0 ? (
          <CircularProggressIndicator />
        ) : (
          <div className={classes.contentContainer}>
            {invoices.length !== 0 ? (
              <InvoiceButtonSection
                downloadClicked={downloadPdfs}
                downloadDisabled={selectedInvoices.length === 0}
                selectAllClicked={selectAllClicked}
                clearAllClicked={clearAllClicked}
                clearAllDisabled={selectedInvoices.length === 0}
              />
            ) : null}
            <div className={classes.invoiceSection}>
              {invoices.length !== 0 ? (
                <InfiniteScroll
                  style={{ overflow: "hidden" }}
                  dataLength={invoices.map((m) => m.Invoices).flat().length} // This is important field to render the next data
                  next={() => loadMore()}
                  scrollThreshold={0.8}
                  hasMore={
                    invoices.map((m) => m.Invoices).flat().length !== total
                  }
                  loader={<CircularProggressIndicator />}
                >
                  {invoices.map((groupItem, index: number) => {
                    return (
                      <InvoiceGroupItem
                        key={index}
                        selectedItems={selectedInvoices}
                        data={groupItem}
                        onCheckboxChange={onCheckboxChange}
                        isAdmin
                      />
                    );
                  })}
                </InfiniteScroll>
              ) : (
                <NoResultsFound
                  query={searchQuery}
                  filterApplied={filterApplied}
                />
              )}
            </div>
          </div>
        )}
      </Container>
    </Box>
  );
};

const filtersApplied = (filter: any) => {
  let applied = false;

  Object.values(filter).forEach((x: any) => {
    if (x.length > 0) {
      applied = true;
    }
  });
  return applied;
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    flexGrow: 1,
    maxWidth: 840,
    paddingLeft: 0,
    paddingRight: 0,
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    marginRight: 24,
    alignSelf: "center",
  },
  contentContainer: {
    //
  },
  invoiceSection: {
    //
  },

  textButton: {
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
    "&:active": {
      backgroundColor: theme.palette.secondary[24],
    },
  },
  printButtonText: {
    textTransform: "none",
  },
  noSearchResults: {
    display: "flex",
    paddingLeft: 24,
    paddingTop: 40,
    flexDirection: "column",
  },
  noRecordsFound: {
    display: "flex",
    paddingLeft: "24px",
    flexDirection: "column",
    paddingTop: "96px",
  },
});

export default AdminInvoices;
